import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Icon } from "../components/Icon";
import { PageTitle } from "../components/PageTitle";
import { Button, EmptyState, Field, Input } from "../components/ui";
import { useToast } from "../components/Toast";
import { apiGet } from "../lib/api";
import { downloadProjectsCsv } from "../lib/projectCsvExport";
import {
  PDR_PROFILE_COMPLETE,
  fieldLabel,
  fieldReasoning,
  fieldValue,
  tableFieldKeys,
  type ProjectData,
} from "../lib/pdrFields";
import { formatApiError } from "../lib/utils";

type ExportRow = {
  project_id: string;
  project_slug: string;
  project_name: string;
  target_company: string;
  vendor_name: string;
  last_updated_at: string;
  project_data?: ProjectData;
};

/** Narrative fields — wrap in place rather than truncating sideways. */
const LONG_TEXT_KEYS = new Set([
  "project_description",
  "project_objective",
  "project_challenge",
  "project_roadmap",
  "project_family_justification",
  "project_family_justification_internal",
  "project_accuracy_justification",
  "project_accuracy_unverified_claims",
]);

/** Comma/semicolon-separated fields — render as chip lists. */
const CHIP_LIST_KEYS = new Set([
  "project_tags",
  "involved_organizations",
  "involved_systems_applications",
  "customer_facing_apps",
  "likely_responsible_roles",
  "business_drivers",
  "related_regulation",
  "common_email_formats",
]);

/** Comma/semicolon-separated fields that often contain URLs. */
const LINK_LIST_KEYS = new Set(["sources", "project_document"]);

const NUMERIC_BADGE_KEYS = new Set(["deal_score", "project_accuracy_score"]);

function splitList(value: string): string[] {
  return value
    .split(/[,;]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function isUrl(s: string): boolean {
  return /^https?:\/\//i.test(s.trim());
}

function riskBadgeClass(value: string): string {
  const v = value.trim().toLowerCase();
  if (v === "high") return "bg-error/10 text-error border-error/30";
  if (v === "medium") return "bg-tertiary/10 text-tertiary border-tertiary/30";
  if (v === "low") return "bg-secondary/10 text-secondary border-secondary/30";
  return "bg-surface-container-high text-on-surface-variant border-outline-variant";
}

function statusBadgeClass(value: string): string {
  const v = value.trim().toLowerCase();
  if (v === "active") return "bg-primary/10 text-primary border-primary/30";
  if (v === "completed") return "bg-secondary/10 text-secondary border-secondary/30";
  if (v === "on hold" || v === "paused") return "bg-tertiary/10 text-tertiary border-tertiary/30";
  return "bg-surface-container-high text-on-surface-variant border-outline-variant";
}

function cellWidthClass(key: string): string {
  if (LONG_TEXT_KEYS.has(key)) return "min-w-[260px] max-w-[360px]";
  if (CHIP_LIST_KEYS.has(key) || LINK_LIST_KEYS.has(key)) return "min-w-[200px] max-w-[280px]";
  return "min-w-[130px] max-w-[220px]";
}

function ExportCell({ fieldKey, data }: { fieldKey: string; data: ProjectData }) {
  const value = fieldValue(data, fieldKey);
  const reasoning = fieldReasoning(data, fieldKey);
  const title = reasoning || undefined;

  if (!value.trim()) {
    return <span className="text-on-surface-variant/50 italic text-xs">—</span>;
  }

  if (fieldKey === "project_risk_level") {
    return (
      <span
        title={title}
        className={`text-[10px] font-bold px-2 py-0.5 rounded border uppercase ${riskBadgeClass(value)}`}
      >
        {value}
      </span>
    );
  }
  if (fieldKey === "status") {
    return (
      <span
        title={title}
        className={`text-[10px] font-bold px-2 py-0.5 rounded border uppercase ${statusBadgeClass(value)}`}
      >
        {value}
      </span>
    );
  }
  if (NUMERIC_BADGE_KEYS.has(fieldKey)) {
    return (
      <span
        title={title}
        className="text-xs font-bold bg-secondary/15 text-secondary px-2 py-0.5 rounded"
      >
        {value}
      </span>
    );
  }
  if (CHIP_LIST_KEYS.has(fieldKey)) {
    return (
      <div className="flex flex-wrap gap-1" title={title}>
        {splitList(value).map((chip, i) => (
          <span
            key={`${chip}-${i}`}
            className="text-[10px] px-1.5 py-0.5 rounded border border-outline-variant bg-surface-container-high text-on-surface-variant"
          >
            {chip}
          </span>
        ))}
      </div>
    );
  }
  if (LINK_LIST_KEYS.has(fieldKey)) {
    return (
      <div className="flex flex-col gap-1" title={title}>
        {splitList(value).map((item, i) =>
          isUrl(item) ? (
            <a
              key={`${item}-${i}`}
              href={item}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary text-xs hover:underline truncate block max-w-[260px]"
            >
              {item}
            </a>
          ) : (
            <span key={`${item}-${i}`} className="text-xs text-on-surface-variant">
              {item}
            </span>
          ),
        )}
      </div>
    );
  }
  if (LONG_TEXT_KEYS.has(fieldKey)) {
    return (
      <p title={title} className="text-xs leading-relaxed whitespace-pre-wrap text-on-surface/90">
        {value}
      </p>
    );
  }
  return (
    <span title={title} className="text-xs font-data-mono text-on-surface/90">
      {value}
    </span>
  );
}

/** Full PDR record set for one company, rendered as a styled spreadsheet-like table. */
export function CompanyExportPage() {
  const { toast } = useToast();
  const [params, setParams] = useSearchParams();
  const target = params.get("target") || "";

  const [targetInput, setTargetInput] = useState(target);
  const [projects, setProjects] = useState<ExportRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exportingCsv, setExportingCsv] = useState(false);
  // PDR Chapter 2: duplicates stay traceable on the Projects page but are out of the dataset.
  const [includeDuplicates, setIncludeDuplicates] = useState(false);

  useEffect(() => {
    setTargetInput(target);
  }, [target]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const q = new URLSearchParams();
    if (target.trim()) q.set("target", target.trim());
    q.set("limit", "500");
    apiGet<{ projects: ExportRow[]; total: number }>(`/api/projects?${q}`)
      .then((data) => {
        if (cancelled) return;
        setProjects(data.projects || []);
        setTotal(data.total ?? 0);
      })
      .catch((e) => {
        if (cancelled) return;
        toast(formatApiError(e), true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [target, toast]);

  const duplicateCount = useMemo(
    () => projects.filter((p) => fieldValue(p.project_data || {}, "project_family").trim().toUpperCase() === "DUPLICATION").length,
    [projects],
  );
  const shown = useMemo(
    () =>
      includeDuplicates
        ? projects
        : projects.filter((p) => fieldValue(p.project_data || {}, "project_family").trim().toUpperCase() !== "DUPLICATION"),
    [projects, includeDuplicates],
  );

  const columns = useMemo(
    () =>
      tableFieldKeys(
        PDR_PROFILE_COMPLETE,
        shown.map((p) => ({ generated_pdr: p.project_data })),
      ),
    [shown],
  );

  const applyTarget = () => {
    const next = new URLSearchParams(params);
    const t = targetInput.trim();
    if (t) next.set("target", t);
    else next.delete("target");
    setParams(next, { replace: true });
  };

  const exportCsv = async () => {
    setExportingCsv(true);
    try {
      const { filename, savedPath } = await downloadProjectsCsv(target, { includeDuplicates });
      toast(`Downloaded ${filename}.${savedPath ? ` Saved to ${savedPath}.` : ""}`);
    } catch (e) {
      toast(e instanceof Error ? e.message : "CSV export failed", true);
    } finally {
      setExportingCsv(false);
    }
  };

  return (
    <main className="flex-1 flex flex-col h-full overflow-hidden min-w-0 bg-background print:h-auto print:overflow-visible">
      <div className="shrink-0 border-b border-outline-variant bg-surface-container print:hidden">
        <PageTitle
          phaseNum={4}
          phaseLabel="Projects"
          title={target ? `Company export — ${target}` : "Company export"}
          meta={`${shown.length === total ? total : `${shown.length} of ${total}`} project${total === 1 ? "" : "s"} · all PDR fields, one row per project`}
          actions={
            <>
              <Link
                to={target ? `/projects?target=${encodeURIComponent(target)}` : "/projects"}
                className="inline-flex items-center gap-1 text-primary text-xs hover:underline"
              >
                <Icon name="arrow_back" className="text-sm" />
                Back to projects
              </Link>
              <Button variant="secondary" onClick={() => window.print()} disabled={!shown.length}>
                <Icon name="print" className="text-base" />
                Print / PDF
              </Button>
              <Button
                variant="primary"
                onClick={() => void exportCsv()}
                disabled={!shown.length || exportingCsv}
              >
                {exportingCsv ? "Exporting…" : "Download CSV"}
              </Button>
            </>
          }
        />
        <div className="px-6 pb-4 flex flex-wrap gap-3 items-end border-t border-outline-variant/40 pt-3">
          <Field label="Company" className="min-w-[240px]">
            <Input
              value={targetInput}
              onChange={(e) => setTargetInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyTarget()}
              placeholder="e.g. ABN AMRO Bank N.V."
            />
          </Field>
          <Button variant="outline" onClick={applyTarget}>
            Load
          </Button>
          <label
            className="flex items-center gap-2 text-body-sm text-on-surface-variant pb-2"
            title="Projects the family pass marked DUPLICATION. They are excluded from the dataset by default, in the table and in the CSV."
          >
            <input
              type="checkbox"
              checked={includeDuplicates}
              onChange={(e) => setIncludeDuplicates(e.target.checked)}
            />
            Include duplicates{duplicateCount ? ` (${duplicateCount})` : ""}
          </label>
        </div>
      </div>

      <div className="flex-1 overflow-auto custom-scrollbar p-4 print:overflow-visible print:p-0">
        {loading ? (
          <p className="p-4 text-body-sm text-on-surface-variant">Loading…</p>
        ) : !shown.length ? (
          <EmptyState
            title="No projects found"
            description="Enter a company name above to load its full PDR record set."
          >
            <Icon name="table_view" className="text-5xl text-outline-variant mb-4" />
          </EmptyState>
        ) : (
          <table className="border-collapse text-sm w-max min-w-full print:w-full">
            <thead className="sticky top-0 z-20 bg-surface-container-high/95 backdrop-blur-sm print:static">
              <tr>
                <th className="p-3 text-left border-b border-r border-outline-variant font-data-label uppercase text-[10px] tracking-widest text-on-surface-variant sticky left-0 z-30 bg-surface-container-high shadow-[4px_0_8px_-4px_rgba(0,0,0,0.35)] print:static print:shadow-none min-w-[220px]">
                  Project
                </th>
                {columns.map((key) => (
                  <th
                    key={key}
                    className={`p-3 text-left border-b border-r border-outline-variant font-data-label uppercase text-[10px] tracking-widest text-on-surface-variant last:border-r-0 ${cellWidthClass(key)}`}
                  >
                    {fieldLabel(key)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant">
              {shown.map((p, i) => (
                <tr key={p.project_id} className={i % 2 === 1 ? "bg-surface-container-low/40" : undefined}>
                  <td className="p-3 border-r border-outline-variant align-top sticky left-0 z-10 bg-surface-container-lowest shadow-[4px_0_8px_-4px_rgba(0,0,0,0.35)] print:static print:shadow-none min-w-[220px]">
                    <p className="text-body-sm font-semibold text-on-surface">{p.project_name}</p>
                    <p className="text-[10px] font-data-mono text-primary mt-0.5">{p.target_company}</p>
                    {p.vendor_name ? (
                      <span className="inline-block mt-1.5 text-[9px] font-bold px-2 py-0.5 rounded border uppercase bg-primary/10 text-primary border-primary/20">
                        {p.vendor_name}
                      </span>
                    ) : null}
                    <p className="text-[10px] text-on-surface-variant font-data-mono mt-1.5">
                      {p.last_updated_at ? new Date(p.last_updated_at).toLocaleDateString() : "—"}
                    </p>
                  </td>
                  {columns.map((key) => (
                    <td
                      key={key}
                      className={`p-3 border-r border-outline-variant align-top last:border-r-0 ${cellWidthClass(key)}`}
                    >
                      <ExportCell fieldKey={key} data={p.project_data || {}} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </main>
  );
}
